import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import { ActionsDropdown } from 'components/common/ActionsDropdown/ActionsDropdown';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { EmailCampaign } from '../types';
import { CAMPAIGN_STATUS_LABELS, formatDate, formatNumber, formatPercent, formatRelative } from '../utils';
import { StatusPill } from '../UI/StatusPill/StatusPill';
import { buildCampaignActions, CAMPAIGN_STATUS_TONES } from './campaignActions';
import styles from './CampaignCardList.module.scss';

interface CampaignCardListProps {
    campaigns: Array<EmailCampaign>;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onOpen: (campaign: EmailCampaign) => void;
}

// Mobile (≤600px) replacement for the campaigns table.
export const CampaignCardList: React.FC<CampaignCardListProps> = ({
    campaigns,
    page,
    totalPages,
    onPageChange,
    onOpen
}) => {
    const { duplicateCampaign, deleteCampaign, describeLocations } = useEmailMarketing();

    return (
        <div className={styles.root}>
            {campaigns.map(campaign => (
                <article
                    key={campaign.id}
                    className={styles.card}
                    onClick={() => onOpen(campaign)}
                >
                    <div className={styles.top}>
                        <StatusPill
                            label={CAMPAIGN_STATUS_LABELS[campaign.status]}
                            tone={CAMPAIGN_STATUS_TONES[campaign.status]}
                        />
                        <div onClick={event => event.stopPropagation()}>
                            <ActionsDropdown
                                direction="down"
                                options={buildCampaignActions(campaign, {
                                    onOpen,
                                    onDuplicate: duplicateCampaign,
                                    onDelete: deleteCampaign
                                })}
                            />
                        </div>
                    </div>

                    <h3 className={styles.name}>{campaign.name}</h3>
                    <p className={styles.subject}>{campaign.subject || 'No subject yet'}</p>

                    <div className={styles.meta}>
                        <span>
                            <FontAwesomeIcon icon={faLocationDot} /> {describeLocations(campaign.locationIds)}
                        </span>
                        <span>
                            {campaign.sentAt
                                ? formatDate(campaign.sentAt)
                                : campaign.updatedAt
                                ? `Edited ${formatRelative(campaign.updatedAt)}`
                                : 'Draft'}
                        </span>
                    </div>

                    {campaign.status === 'sent' && (
                        <dl className={styles.stats}>
                            <div>
                                <dt>Recipients</dt>
                                <dd>{formatNumber(campaign.recipients)}</dd>
                            </div>
                            <div>
                                <dt>Open rate</dt>
                                <dd>{formatPercent(campaign.openRate)}</dd>
                            </div>
                            <div>
                                <dt>Click rate</dt>
                                <dd>{formatPercent(campaign.clickRate)}</dd>
                            </div>
                        </dl>
                    )}
                </article>
            ))}

            {totalPages > 1 && (
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
};

export default CampaignCardList;
